import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../locales/en';
import ru from '../locales/ru';
import uz from '../locales/uz';

const translations = { en, ru, uz };

const LanguageContext = createContext();

export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺', nativeName: 'Русский' },
  { code: 'uz', name: 'Uzbek', flag: '🇺🇿', nativeName: 'O\'zbek' },
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language from localStorage or default to 'en'
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const saved = localStorage.getItem('aiconneqtors_language');
        return saved && translations[saved] ? saved : 'en';
      }
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
    return 'en';
  });

  useEffect(() => {
    try {
      localStorage.setItem('aiconneqtors_language', language);
      // Update HTML lang attribute
      document.documentElement.lang = language;
    } catch (e) {
      console.warn('Could not save language preference:', e);
    }
  }, [language]);

  const t = (key, params = {}) => {
    if (!key || typeof key !== 'string') {
      return key || '';
    }
    
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = translations['en'];
        for (const k2 of keys) {
          if (value && typeof value === 'object' && k2 in value) {
            value = value[k2];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }
    
    // Return the value as-is if it's an object or array (for complex translations)
    if (value !== null && value !== undefined && (typeof value === 'object' || Array.isArray(value))) {
      return value;
    }
    
    if (typeof value !== 'string') {
      return key;
    }
    
    // Replace parameters like {name} with actual values
    let result = value;
    Object.keys(params).forEach(param => {
      result = result.replace(new RegExp(`{${param}}`, 'g'), params[param]);
    });
    
    return result;
  };

  const changeLanguage = (langCode) => {
    if (translations[langCode]) {
      setLanguage(langCode);
    }
  };

  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: changeLanguage, 
      t, 
      currentLanguage,
      languages 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
